/**
 * What a crawler needs for a comparison article. The page itself is a single-page app, so the
 * Worker writes these into the HTML. The slug names both products. `former` is the old one-name
 * slug, which redirects.
 */
import { z } from "zod";

const Card = z.object({
  title: z.string(),
  description: z.string(),
  image: z.string(),
});

const Page = Card.extend({
  slug: z.string(),
  former: z.string(),
});

export const ComparePreviews = z.object({
  index: Card,
  pages: z.array(Page),
});

export type CompareCard = z.infer<typeof Card>;

export type ComparePreviewPage = z.infer<typeof Page>;

export type ComparePreviews = z.infer<typeof ComparePreviews>;

export interface CompareMeta extends CompareCard {
  readonly url: string;
}

const INDEX_PATH = "/compare";

function escapeAttr(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll('"', "&quot;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;");
}

export function previewPage(
  pathname: string,
  previews: ComparePreviews,
): ComparePreviewPage | undefined {
  const slug = pathname.slice(`${INDEX_PATH}/`.length);

  for (const page of previews.pages) if (page.slug === slug) return page;

  return undefined;
}

/** Old one-name slugs redirect to the article whose slug names both products. */
export function compareRedirect(pathname: string, previews: ComparePreviews): string | null {
  if (!pathname.startsWith(`${INDEX_PATH}/`)) return null;
  const slug = pathname.slice(`${INDEX_PATH}/`.length);

  if (slug === "" || slug.includes("/")) return null;

  for (const page of previews.pages) {
    if (page.former === slug) return `${INDEX_PATH}/${page.slug}`;
  }

  return null;
}

export function compareMeta(
  pathname: string,
  previews: ComparePreviews,
  origin: string,
): CompareMeta {
  const page = pathname === INDEX_PATH ? previews.index : previewPage(pathname, previews);

  const card = page ?? {
    title: "Comparison not found",
    description: "That page is not in the list.",
    image: previews.index.image,
  };

  return {
    title: card.title,
    description: card.description,
    image: `${origin}${card.image}`,
    url: `${origin}${pathname}`,
  };
}

/** Inserts the tags a crawler reads. Replaces the document title and description. */
export function withCompareMeta(html: string, meta: CompareMeta): string {
  const title = escapeAttr(meta.title);
  const description = escapeAttr(meta.description);
  const image = escapeAttr(meta.image);
  const url = escapeAttr(meta.url);

  const block = [
    `<title>${title}</title>`,
    `<meta name="description" content="${description}" />`,
    `<meta property="og:type" content="article" />`,
    `<meta property="og:title" content="${title}" />`,
    `<meta property="og:description" content="${description}" />`,
    `<meta property="og:image" content="${image}" />`,
    `<meta property="og:url" content="${url}" />`,
    `<meta name="twitter:card" content="summary_large_image" />`,
    `<meta name="twitter:title" content="${title}" />`,
    `<meta name="twitter:description" content="${description}" />`,
    `<meta name="twitter:image" content="${image}" />`,
    `<link rel="canonical" href="${url}" />`,
  ].join("");

  const withoutTitle = html.replace(/<title>[^<]*<\/title>/, "");
  const withoutDescription = withoutTitle.replace(/<meta\s+name="description"[^>]*>/, "");

  return withoutDescription.replace("</head>", `${block}</head>`);
}
